import React, { useEffect } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLoginFlow } from '@/hooks/useLoginFlow'
import { rememberPostLoginRedirect, consumePostLoginRedirect } from '@/utils/postLoginRedirect'
import { startLoginJourney } from '@/utils/loginJourney'
import LoginIdentifierStep from '@/components/auth/login/LoginIdentifierStep'
import LoginPasswordStep from '@/components/auth/login/LoginPasswordStep'
import LoginClaimStep from '@/components/auth/login/LoginClaimStep'
import LoginAddEmailStep from '@/components/auth/login/LoginAddEmailStep'
import LoginAddEmailVerifyStep from '@/components/auth/login/LoginAddEmailVerifyStep'
import LoginSetPasswordStep from '@/components/auth/login/LoginSetPasswordStep'
import OutageNotice from '@/components/common/OutageNotice'
import LoginNotFoundStep from '@/components/auth/login/LoginNotFoundStep'
import LoginLandlineStep from '@/components/auth/login/LoginLandlineStep'
import LoginBusinessBlockedStep from '@/components/auth/login/LoginBusinessBlockedStep'
import MagicLinkSentStep from '@/components/auth/login/MagicLinkSentStep'
import {
  AUTH_MAINTENANCE_BODY,
  AUTH_MAINTENANCE_ENABLED,
} from '@/config/maintenance'
import '@/styles/pages/login.css'

/**
 * Two-column login shell.
 *
 *   Left  — brand panel: logo, a one-line promise of value, a short list
 *           of what the customer can do once signed in. Reassurance copy
 *           that's missing from the old login form.
 *   Right — the active step in the login flow.
 *
 * The flow itself (which step is current, which API call to make next,
 * how state transitions on success/failure) lives in `useLoginFlow`.
 * This component is a router over `state.step.kind` and a layout shell.
 *
 * Already-logged-in users get redirected away from /login. The redirect
 * target honours `?redirect=` from RequireAuth so a deep-link expiry
 * round-trip lands the customer back where they were.
 */

// Only ever honour an internal path as the post-login destination, never an
// absolute or protocol-relative URL, so a crafted ?redirect= can't bounce the
// customer off-site (DF-230).
const safeRedirect = (raw: string | null): string =>
  raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/'

function LoginPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAuth()
  const { state, derived, actions } = useLoginFlow()

  // Stash the intended path so it survives a magic-link email round-trip, which
  // returns on a fresh page carrying only a token and no ?redirect= (DF-230).
  useEffect(() => {
    rememberPostLoginRedirect(searchParams.get('redirect'))
  }, [searchParams])

  // A visit to the login page is the start of a login journey, so open a fresh
  // one. Every auth request in this visit then carries its id, and the backend
  // groups them into one login_attempts row (DF-352).
  useEffect(() => {
    startLoginJourney()
  }, [])

  // If a fresh sign-in just happened (claim or password) RequireAuth
  // doesn't bounce us back; the auth context updates underneath us.
  // Watch for that and navigate. Prefer the live ?redirect= param, fall back to
  // the stashed path, and clear the stash either way.
  useEffect(() => {
    if (isAuthenticated && state.step.kind !== 'identifier') {
      const stashed = consumePostLoginRedirect()
      navigate(safeRedirect(searchParams.get('redirect') ?? stashed), { replace: true })
    }
  }, [isAuthenticated, state.step.kind, navigate, searchParams])

  // Already signed in on page load — short-circuit to the intended path.
  if (isAuthenticated && state.step.kind === 'identifier') {
    return <Navigate to={safeRedirect(searchParams.get('redirect'))} replace />
  }

  return (
    <div className="login-page-wrap">
      {/*
        Content area takes whatever vertical space remains after the
        footer below it. Flex-centres the popup both horizontally and
        vertically so the card sits true centre on tall viewports
        regardless of the content's height.
      */}
      <div className="login-page-content">
        <div className="login-page">
          <aside className="login-page-panel">
            <div className="login-page-panel-inner">
              <img
                src="/mcqueens-logo.png"
                alt="McQueens Dairies"
                className="login-page-logo"
              />
              <h2 className="login-page-tagline">Your milk delivery, online.</h2>
              <ul className="login-page-bullets">
                <li>Pause and resume deliveries any time.</li>
                <li>Change your weekly order in seconds.</li>
                <li>Update your address, payment details, and contact info.</li>
                <li>See what's coming and when.</li>
              </ul>
            </div>
          </aside>

          <main className="login-page-main">
            <div className="login-page-step-container">
              {/* Set by apiClient when a call came back 401 with a token: the
                  session ended rather than the customer choosing to leave, so
                  say so instead of presenting a bare sign-in form.

                  Inside the step container, not beside it: .login-page-main is
                  a centring flex row, so a sibling here lays out next to the
                  form rather than above it. */}
              {searchParams.get('expired') === '1' && (
                <p className="login-session-expired" role="status">
                  Your session has ended. Please sign in again.
                </p>
              )}
              {AUTH_MAINTENANCE_ENABLED ? (
                <div className="login-step">
                  <h1 className="login-step-heading">
                    Sign-in temporarily unavailable
                  </h1>
                  <p
                    className="login-step-subheading"
                    role="status"
                    style={{ lineHeight: 1.6 }}
                  >
                    {AUTH_MAINTENANCE_BODY}
                  </p>
                </div>
              ) : state.isOutage ? (
                /* FEDEV-528: Dynamics is unreachable. Shown instead of the
                   step rather than alongside it, because signing in would
                   only lead to an app that fails on every screen. */
                <div className="login-step">
                  <h1 className="login-step-heading">We are having some problems</h1>
                  <OutageNotice message={state.apiError} onRetry={actions.back} />
                </div>
              ) : (
                renderStep(state, derived, actions)
              )}
            </div>
          </main>
        </div>
      </div>

      {/*
        Compact footer pinned to the bottom of the page (the wrap is
        flex-column with min-height: 100vh, and the content above
        flex: 1, so this row gets pushed down). Mirrors the global
        Footer's links (Privacy / T&Cs / Contact) plus a copyright
        line, but without the heavyweight footer chrome — the login
        page's job is to get the customer into the portal, not to be
        a website.
      */}
      <footer className="login-page-footer">
        <p>&copy; {new Date().getFullYear()} McQueens Dairies</p>
        <nav>
          <a
            href="https://www.mcqueensdairies.co.uk/privacy-and-cookies-policy/"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          <a
            href="https://www.mcqueensdairies.co.uk/terms-and-conditions/"
            target="_blank"
            rel="noreferrer"
          >
            Terms and Conditions
          </a>
          <a
            href="https://www.mcqueensdairies.co.uk/help/"
            target="_blank"
            rel="noreferrer"
          >
            Contact Us
          </a>
        </nav>
      </footer>
    </div>
  )
}

/**
 * Switch over the flow's current step. Kept out of the component body
 * so the JSX above stays readable; each branch is a thin pass-through
 * to the relevant step component.
 */
function renderStep(
  state: ReturnType<typeof useLoginFlow>['state'],
  derived: ReturnType<typeof useLoginFlow>['derived'],
  actions: ReturnType<typeof useLoginFlow>['actions'],
): React.ReactNode {
  // Note: there is intentionally NO global loading-spinner branch
  // here. Replacing the step with a spinner during the identity-check
  // round-trip caused the popup to collapse to spinner-only height,
  // jarring on a tablet screen. Each step component instead renders
  // its own in-button "loading" state ("Checking…", "Signing in…",
  // etc.) which keeps the surrounding chrome stable.
  switch (state.step.kind) {
    case 'identifier':
      return (
        <LoginIdentifierStep
          identifier={state.identifier}
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          onIdentifierChange={actions.setIdentifier}
          onSubmit={actions.checkIdentity}
        />
      )
    case 'login':
      return (
        <LoginPasswordStep
          identifier={state.identifier}
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          channels={state.channels ?? []}
          onSignIn={actions.signIn}
          onRequestMagicLink={actions.requestMagicLink}
          onBack={actions.back}
        />
      )
    case 'register':
      return (
        <LoginClaimStep
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          channels={state.step.channels}
          onRequestCode={actions.requestClaimCode}
          onVerifyCode={actions.verifyClaimCode}
          onBack={actions.back}
        />
      )
    case 'add-email':
      return (
        <LoginAddEmailStep
          identifier={state.identifier}
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          onSubmit={actions.requestAddEmailCode}
          onBack={actions.back}
        />
      )
    case 'add-email-verify':
      return (
        <LoginAddEmailVerifyStep
          identifier={state.identifier}
          pendingEmail={state.step.pendingEmail}
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          onSubmit={actions.verifyAddEmail}
          onResend={() => actions.requestAddEmailCode(state.step.kind === 'add-email-verify' ? state.step.pendingEmail : '')}
          onBack={actions.back}
        />
      )
    case 'set-password':
      return (
        <LoginSetPasswordStep
          loading={state.loading}
          apiError={state.apiError}
          fieldErrors={state.fieldErrors}
          onSubmit={actions.claimAccount}
          onBack={actions.back}
        />
      )
    // They typed a landline. Named outright, unlike the dead ends below,
    // because it is a fact about the number and not about any account.
    case 'landline':
      return <LoginLandlineStep onBack={actions.back} />
    // A business (non-residential) customer. Blocked server-side; shown its own
    // screen with the reason and a way to get help (FEDEV-565).
    case 'business-blocked':
      return <LoginBusinessBlockedStep onBack={actions.back} />
    // Every dead end, including a mobile on more than one customer record and
    // an account with only a landline. One screen on purpose: see
    // DeadEndReason in useLoginFlow.
    case 'not-found':
      return (
        <LoginNotFoundStep
          identifier={state.identifier}
          triedIdentifierType={state.step.triedIdentifierType}
          untriedIdentifierTypes={derived.untriedIdentifierTypes}
          hasTriedAllIdentifierTypes={derived.hasTriedAllIdentifierTypes}
          reason={state.step.reason}
          onBack={actions.back}
        />
      )
    case 'magic-link-sent':
      return (
        <MagicLinkSentStep
          channel={state.step.channel}
          onResend={actions.requestMagicLink}
          onBack={actions.back}
        />
      )
  }
}

export default LoginPage
